import { ArrowLeft, Atom, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  GROUP_DEFAULT_MODE_LADDER,
  GROUP_REACTIVITY_MODES,
  GROUP_REACTIVITY_PROVENANCE,
  GROUP_REACTIVITY_START_YEAR,
  GROUP_REACTIVITY_TOOL_IDENTITY,
  type GroupReactivityMode,
} from './groupReactivity';
import { AlkaliMode } from './GroupReactivityAlkali';
import { HalogensMode } from './GroupReactivityHalogens';
import { StudyShell } from './StudyShell';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<GroupReactivityMode, { tab: string; eyebrow: string; title: string; description: string }> = {
  alkali: {
    tab: 'Alkali metals',
    eyebrow: 'Group 1 with water',
    title: 'Why does reactivity increase down group 1?',
    description: 'Choose lithium, sodium or potassium, predict what happens in water, then link the trend to the outer electron.',
  },
  halogens: {
    tab: 'Halogens',
    eyebrow: 'Group 17 trends and displacement',
    title: 'Why does reactivity decrease down group 17?',
    description: 'Compare states and colours from fluorine to iodine, then predict which halogen displaces which halide.',
  },
};

export function GroupReactivityLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<GroupReactivityMode>(() => modeForYear(GROUP_DEFAULT_MODE_LADDER, year));
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'group-reactivity', state: { mode } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:p-8"
        data-tool-key={GROUP_REACTIVITY_TOOL_IDENTITY.toolKey}
        data-tool-version={GROUP_REACTIVITY_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Atom className="h-4 w-4" aria-hidden="true" /> Elements and atoms
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Group Reactivity Lab</h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Predict how the alkali metals and the halogens react, then explain each trend with electron shells.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            From Year {GROUP_REACTIVITY_START_YEAR} · two modes
          </div>
        </div>

        <div className="mt-6 grid max-w-xl grid-cols-2 rounded-full bg-white/75 p-1" role="group" aria-label="Choose mode">
          {GROUP_REACTIVITY_MODES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">{copy.eyebrow}</p>
            <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
          </div>
          <div className="shrink-0 rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
            Explore only · not mastery evidence
          </div>
        </div>
      </section>

      {mode === 'alkali' ? <AlkaliMode /> : <HalogensMode />}

      <footer className="my-6 space-y-2 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        <p className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Group 1 metals with water are teacher demonstrations only, with tiny pieces, a safety screen and eye
          protection. Chlorine and bromine are toxic; halogen water is used in small amounts in a fume cupboard or
          well-ventilated room. Follow your school’s risk assessment. This browser model is not a safety guide.
        </p>
        <p>
          Shell diagrams are schematics generated from the electron arrangement, not to scale. Data values are rounded.
          Conditions: {GROUP_REACTIVITY_PROVENANCE.conditions} Sources: {GROUP_REACTIVITY_PROVENANCE.sources.join(' · ')}.
          Reviewed teaching cases (pending teaching review). This tool does not save an attempt, record mastery or claim
          alignment to a state syllabus.
        </p>
      </footer>
    </StudyShell>
  );
}
